import { Box, Button, Tooltip } from '@mui/material';
import {
  GridExpandMoreIcon,
  gridRowNodeSelector,
  GridRenderCellParams,
  GridRowId,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { ConditionsNew } from '@retail/calculus/types';
import { useAppTFunction } from '@retail/app/i18n';

interface Props {
  params: GridRenderCellParams<ConditionsNew>;
}

type GridApiRef = ReturnType<typeof useGridApiContext>;

// Counts every condition nested under a node, at any depth. Real product-group parents
// count too; only auto-generated nodes (a supplier with no supplier-level condition) don't.
const countDescendantConditions = (apiRef: GridApiRef, childIds: GridRowId[]): number =>
  childIds.reduce<number>((count, childId) => {
    const node = gridRowNodeSelector(apiRef, childId);
    const isAutoGenerated = node.type === 'group' && node.isAutoGenerated;
    const children = node.type === 'group' ? countDescendantConditions(apiRef, node.children) : 0;
    return count + (isAutoGenerated ? 0 : 1) + children;
  }, 0);

export const SupplierGroupToggle = ({ params }: Props) => {
  const t = useAppTFunction();
  const apiRef = useGridApiContext();
  const { rowNode, id } = params;

  if (rowNode.type !== 'group') return null;
  const extraConditionsCount = countDescendantConditions(apiRef, rowNode.children);
  if (extraConditionsCount === 0) return null;
  const expanded = !!rowNode.childrenExpanded;

  return (
    <Button
      size="small"
      aria-label={expanded ? t('calculus.close') : t('calculus.open')}
      onClick={() => apiRef.current.setRowChildrenExpansion(id, !expanded)}
      sx={{
        py: 0.25,
        '&:hover': {
          backgroundColor: 'action.hover',
          color: 'text.primary',
          border: (theme) => `1px solid ${theme.vars.palette.action.hover}`,
          borderRadius: 40,
        },
      }}
      endIcon={
        <Box
          component="span"
          sx={(theme) => ({
            display: 'inline-flex',
            transform: `rotate(${expanded ? 0 : -90}deg)`,
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        >
          <GridExpandMoreIcon fontSize="inherit" />
        </Box>
      }
    >
      +{extraConditionsCount}
    </Button>
  );
};
